import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { StepEfficiencyData } from '../../types/dashboard';

interface Props {
  data: StepEfficiencyData | null;
}

const COLORS = {
  allTime: '#6366f1',
  perAction: '#f59e0b',
  recent: '#10b981',
};

export default function StepEfficiencyChart({ data }: Props) {
  if (!data || !data.steps || data.steps.length === 0) {
    return <Placeholder text="No step efficiency data available" />;
  }

  const chartData = data.steps.map((s) => ({
    name: s.step_name || `Step ${s.step_number}`,
    'Avg Time (all)': s.avg_minutes,
    'Avg Time/Action': s.avg_minutes_per_action ?? 0,
    'Avg Time (last 7)': s.avg_minutes_recent ?? 0,
  }));

  return (
    <div style={sectionStyle}>
      <h3 style={headingStyle}>Step Time & Efficiency</h3>
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={chartData} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#9ca3af' } }}
          />
          <Tooltip
            contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' }}
            formatter={(value) => `${Number(value).toFixed(2)} min`}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Avg Time (all)" fill={COLORS.allTime} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Avg Time/Action" fill={COLORS.perAction} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Avg Time (last 7)" fill={COLORS.recent} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: '#9ca3af' }}>
      {text}
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
};

const headingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  margin: '0 0 16px 0',
};
